<template>
  <div>
    <div
      class="pl-8 pr-6"
      :class="overflowClass"
      style="max-height: calc(100vh - 300px); min-height: 130px"
    >
      <!-- Title -->
      <div class="mb-6 w-full">
        <div class="w-full font-semibold text-left text-sm text-grey-700 mb-2">
          {{ $t('frontend_groovevideo_yt_video_title_heading') }}
        </div>
        <div class="w-full text-sm text-left">
          <text-input
            v-model="localVideoModel.name"
            :validator="$v.localVideoModel.name"
            class="text-deep-dark"
            :attribute="$t('frontend_groovevideo_yt_video_title')"
            :placeholder="$t('frontend_groovevideo_yt_video_title_placeholder')"
          />
        </div>
      </div>

      <!-- Description -->
      <div class="mb-6 w-full">
        <div class="w-full font-semibold text-left text-sm text-grey-700 mb-2">
          {{ $t('frontend_groovevideo_yt_video_desc') }}
        </div>
        <div class="w-full text-sm">
          <textarea
            v-model="localModel.description"
            :validator="$v.localModel.description"
            class="
              w-full
              outline-none
              rounded-lg
              px-4
              resize-none
              h-24
              border border-solid border-grey-200
              pt-2
              text-deep-dark
            "
            :attribute="$t('frontend_groovevideo_yt_video_desc')"
            :placeholder="$t('frontend_groovevideo_yt_video_desc_placeholder')"
          ></textarea>
        </div>
      </div>
      <!-- Category -->
      <div class="mb-6 w-full">
        <div class="w-full font-semibold text-left text-sm text-grey-700 mb-2">
          {{ $t('frontend_groovevideo_yt_video_category') }}
        </div>
        <div class="w-full text-sm text-left">
          <div>
            <treeselect
              v-model="localModel.category"
              :validator="$v.localModel.category"
              class="
                h-10
                groovevideo-multilevel-select
                video-category
                w-full
                text-left text-deep-dark
              "
              :multiple="false"
              :options="categories"
              :closeOnSelect="true"
              :clearable="false"
              :clearOnSelect="true"
              :isDefaultExpanded="true"
              :defaultExpandLevel="1"
              :showCount="true"
              :max-height="200"
              :placeholder="
                $t('frontend_groovevideo_yt_video_category_placeholder')
              "
              @open="overflowClass = 'overflow-hidden mr-2'"
              @close="overflowClass = 'overflow-auto'"
            />
          </div>
        </div>
      </div>

      <!-- Tags -->
      <div class="mb-6 w-full">
        <div
          class="w-full mb-1 font-semibold text-left text-sm text-grey-700 mb-2"
        >
          {{ $t('frontend_groovevideo_yt_video_tags') }}
        </div>
        <div class="flex flex-wrap">
          <gd-button
            class="add-tag-button text-xs"
            primary
            @click="openTagModal"
          >
            {{ $t('frontend_groovevideo_yt_video_add_tag_btn') }}
          </gd-button>

          <div
            class="
              mb-2
              ml-2
              px-2
              py-1
              flex
              h-6
              items-center
              rounded
              text-white text-xs
              bg-pink
            "
            v-for="(tag, index) in localModel.tags"
            :key="index"
          >
            <div class="font-semibold mr-2">{{ tag }}</div>
            <div>
              <div class="cursor-pointer" @click="removeTag(index)">
                <i class="fas fa-times"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Privacy -->
      <div class="w-full pb-2">
        <div class="w-full font-semibold text-left text-sm text-grey-700 mb-2">
          {{ $t('frontend_groovevideo_yt_video_privacy') }}
        </div>
        <div class="w-full text-sm text-left">
          <gd-dropdown
            :options="privacyOptions"
            v-model="localModel.privacy"
            :validator="$v.localModel.privacy"
            class="text-left text-deep-dark h-10 custom-selectbox custom-last-selectbox"
            size="full"
            :placeholder="
              $t('frontend_groovevideo_yt_video_privacy_placeholder')
            "
          ></gd-dropdown>
        </div>
      </div>
      <!-- Video details form ends -->
    </div>

    <!-- Add tags modal -->
    <gd-modal
      ref="addTags"
      name="addTags"
      :title="$t('frontend_groovevideo_yt_video_add_tag_modal_title')"
      @closed="resetTagModal"
    >
      <div class="p-6">
        <div class="flex flex-col mb-6">
          <label class="font-semibold text-sm text-grey-700 mb-2">
            {{ $t('frontend_groovevideo_yt_video_add_tag_title') }}
          </label>
          <text-input
            class="text-dark text-sm"
            :validator="$v.newTag"
            v-model="newTag"
            :attribute="this.$t('frontend_groovevideo_yt_video_add_tag_title')"
            value="newTag"
            :placeholder="this.$t('frontend_groovevideo_yt_video_add_tag_desc')"
          />
          <div v-if="tagsError" class="mt-1 ml-1 error">
            {{ $t('frontend_groovevideo_yt_video_add_tag_error') }}
          </div>
        </div>
        <div class="flex justify-end">
          <gd-button
            class="
              h-10
              btn-width
              px-4
              btn-radius
              text-base
              font-semibold
              border-grey-300
            "
            size="auto"
            @click="closeTagModal"
            >{{ $t('frontend_cancel') }}</gd-button
          >
          <gd-button
            class="h-10 btn-width px-4 btn-radius text-base font-semibold ml-2"
            size="auto"
            primary
            @click="addTag"
          >
            {{ $t('frontend_add') }}
          </gd-button>
        </div>
      </div>
    </gd-modal>
  </div>
</template>

<style>
  .add-tag-button {
    height: 24px !important;
    border-radius: 4px !important;
  }

  .custom-last-selectbox .overflow-auto.custom-scrollbars {
    position: absolute;
    bottom: 100% !important;
    box-shadow: 0 0 6px #ccc;
    border-radius: 4px 4px 0 0;
  }
</style>

<script>
import { required, minLength, maxLength } from 'vuelidate/lib/validators';
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';

export default {
  props: {
    model: {
      required: true,
    },
    videoModel: {
      required: true,
    }
  },
  components: {
    Treeselect,
  },
  data() {
    return {
      overflowClass: 'overflow-auto',
      localVideoModel: this.videoModel,
      localModel: this.model,
      newTag: '',
      tagsError: false,
    };
  },
  validations: {
    localVideoModel: {
      name: {
        required,
        minLength: minLength(1),
        maxLength: maxLength(100),
      },
    },
    localModel: {
      description: {
        maxLength: maxLength(5000),
      },
      category: {
        required,
      },
      privacy: {
        required,
      },
    },
    newTag: {
      required,
    },
  },
  computed: {
    privacyOptions() {
      return [
        {
          text: this.$t('frontend_groovevideo_yt_video_privacy_public'),
          value: 'public',
        },
        {
          text: this.$t('frontend_groovevideo_yt_video_privacy_unlisted'),
          value: 'unlisted',
        },
      ];
    },
    categories() {
      return [
        {
          id: 1,
          label: this.$t('frontend_groovevideo_yt_video_category_film'),
        },
        {
          id: 2,
          label: this.$t('frontend_groovevideo_yt_video_category_vehicles'),
        },
        {
          id: 10,
          label: this.$t('frontend_groovevideo_yt_video_category_music'),
        },
        {
          id: 15,
          label: this.$t('frontend_groovevideo_yt_video_category_animals'),
        },
        {
          id: 17,
          label: this.$t('frontend_groovevideo_yt_video_category_sports'),
        },
        {
          id: 19,
          label: this.$t('frontend_groovevideo_yt_video_category_travel'),
        },
        {
          id: 20,
          label: this.$t('frontend_groovevideo_yt_video_category_gaming'),
        },
        {
          id: 22,
          label: this.$t('frontend_groovevideo_yt_video_category_people'),
        },
        {
          id: 23,
          label: this.$t('frontend_groovevideo_yt_video_category_comedy'),
        },
        {
          id: 24,
          label: this.$t('frontend_groovevideo_yt_video_category_entertainment'),
        },
        {
          id: 25,
          label: this.$t('frontend_groovevideo_yt_video_category_politics'),
        },
        {
          id: 26,
          label: this.$t('frontend_groovevideo_yt_video_category_style'),
        },
        {
          id: 27,
          label: this.$t('frontend_groovevideo_yt_video_category_education'),
        },
        {
          id: 28,
          label: this.$t('frontend_groovevideo_yt_video_category_technology'),
        },
        {
          id: 29,
          label: this.$t('frontend_groovevideo_yt_video_category_activism'),
        },
      ];
    },
  },
  methods: {
    openTagModal() {
      this.$root.$emit('modal-open', 'addTags');
    },
    resetTagModal() {
      this.newTag = '';
      this.tagsError = false;
      this.$v.$reset();
    },
    closeTagModal() {
      this.$root.$emit('modal-close', 'addTags');
    },
    addTag() {
      this.$v.newTag.$touch();
      let allTags = this.localModel.tags.join('');
      allTags += this.newTag;
      if (!this.$v.newTag.$invalid) {
        if (allTags.length <= 500) {
          this.localModel.tags.push(this.newTag);
          this.closeTagModal();
        } else {
          this.tagsError = true;
        }
      }
    },
    removeTag(index) {
      this.localModel.tags.splice(index, 1);
    },
    validate() {
      this.$v.localModel.$touch();
      return !this.$v.localModel.$invalid;
    },
  },
};
</script>